import React, { useState, useEffect, useRef } from 'react'
import { inject, observer } from 'mobx-react'
import { StateType } from '../state'
import { sumBy, sortBy, tail } from 'lodash-es'
import { computeCircularSegments } from 'divide-up-circle-in-circular-segments'
import { SliderStep } from '../../../components/ui/SliderStep'
import gsap from 'gsap'
import { formatPercentageUniversal } from '../../../lib/format-utils'
import { Container } from './Container'

const RADIUS = 150
const SPACE_BETWEEN_LABEL_AND_CIRCLE = 50
const VERTICAL_TEXT_SPACE = 30
const DIMENSIONS = {
  w: RADIUS * 2,
  h: RADIUS * 2 + SPACE_BETWEEN_LABEL_AND_CIRCLE + VERTICAL_TEXT_SPACE,
}
const MARGINS = { top: DIMENSIONS.h - RADIUS * 2, right: 0, bottom: 0, left: 0 }
const GRAY = 'var(--gray100)'

interface Props {
  className?: string
  state?: StateType
}

export const CirclesSubsectors: React.FC<Props> = inject('state')(
  observer(({ className = '', state }) => {
    const { activeSubsectors: _activeSubsectors, challenge, debug } = state.Circles
    const subsectors = sortBy(_activeSubsectors, (d) => -d.emission_mmt_co2)

    const center = { x: RADIUS, y: RADIUS }

    const emissionSum = sumBy(subsectors, 'emission_mmt_co2')
    const subsectorsWithPercentages = subsectors.map((s) => ({
      ...s,
      percentage: s.emission_mmt_co2 / emissionSum,
    }))

    const circularSegmentsInfo = computeCircularSegments(
      subsectorsWithPercentages,
      RADIUS,
      center,
      {
        orientation: 'vertical',
      }
    )

    const [selectedSubsectorIndex, setSelectedSubsectorIndex] = useState(0)
    const [hoveredSubsectorIndex, setHoveredSubsectorIndex] = useState(null)

    const animatedMask = useRef(null)
    const subsectorsContainer = useRef(null)
    useEffect(() => {
      const sectorsToAnimate = tail(
        Array.from(subsectorsContainer.current.querySelectorAll('path'))
      )
      gsap
        .timeline({
          onComplete: () => {
            setSelectedSubsectorIndex(0)
          },
        })
        .set(sectorsToAnimate, {
          attr: { fill: (i) => circularSegmentsInfo[i].color, 'stroke-width': 0 },
        })
        .fromTo(
          animatedMask.current,
          {
            translateX: 0,
          },
          {
            translateX: DIMENSIONS.w,
            duration: 0.5,
            ease: 'power1.in',
          }
        )
        .to(sectorsToAnimate, {
          attr: { fill: '#eceff2', 'stroke-width': 1 },
          stagger: { each: 0.1 },
        })
    }, [challenge])

    return (
      <Container>
        <div
          className={`${debug ? 'ba b--red' : ''} ${className}`}
          style={{ width: DIMENSIONS.w, height: DIMENSIONS.h }}
        >
          <svg width={DIMENSIONS.w} height={DIMENSIONS.h}>
            {debug && (
              <g className="-debug">
                <rect
                  x={0}
                  y={0}
                  width={DIMENSIONS.w}
                  height={DIMENSIONS.h}
                  fill="none"
                  stroke="black"
                />
                <rect
                  x={0}
                  y={MARGINS.top}
                  width={DIMENSIONS.w}
                  height={DIMENSIONS.h - MARGINS.top}
                  fill="none"
                  stroke="green"
                />
                <circle cx={center.x} cy={center.y} r={2} fill="black" />
              </g>
            )}

            <g
              ref={subsectorsContainer}
              className="-subsectors"
              transform={`translate(0, ${MARGINS.top})`}
            >
              {circularSegmentsInfo.map((circularSegment, i) => {
                const { percentage, path, color } = circularSegment

                const isHovered = hoveredSubsectorIndex === i
                const isSelected = selectedSubsectorIndex === i
                const fill = isSelected ? color : GRAY
                const percentageValue = formatPercentageUniversal(percentage * 100)
                const subsectorName = subsectorsWithPercentages[i].subsector

                return (
                  <g key={subsectorName}>
                    {isSelected && (
                      <text
                        className="f90"
                        x={center.x}
                        y={center.y - RADIUS - SPACE_BETWEEN_LABEL_AND_CIRCLE}
                        fill="black"
                        textAnchor="middle"
                        dominantBaseline="baseline"
                      >
                        <tspan>{subsectorName}</tspan>{' '}
                        <tspan fontWeight="bold">{percentageValue}</tspan>
                      </text>
                    )}

                    <path
                      className="-sector pointer"
                      d={path}
                      stroke={'white'}
                      strokeWidth={1}
                      fill={fill}
                      fillOpacity={isHovered ? (isSelected ? 1 : 0.7) : 1}
                      onClick={() => setSelectedSubsectorIndex(i)}
                      onMouseMove={() => setHoveredSubsectorIndex(i)}
                      onMouseLeave={() => setHoveredSubsectorIndex(null)}
                    />
                  </g>
                )
              })}
            </g>

            <rect
              ref={animatedMask}
              className="-animated-mask"
              x={MARGINS.left}
              y={MARGINS.top}
              width={RADIUS * 2}
              height={RADIUS * 2}
              fill="white"
            />
          </svg>
        </div>

        <div
          className={`flex flex-column items-center mt1 pt3 ${debug ? 'ba' : ''}`}
          style={{ width: DIMENSIONS.w, paddingBottom: 23 }}
        >
          <SliderStep
            className=""
            stepsCounter={subsectors.length}
            selectedStep={selectedSubsectorIndex}
            onChange={setSelectedSubsectorIndex}
          />
        </div>
      </Container>
    )
  })
)
